/**
 * Centralized date formatting utilities for consistent date display across the app.
 * All functions handle Date, string (ISO), or number (timestamp) inputs.
 * Uses en-US locale for consistent formatting regardless of browser settings.
 */

type DateInput = Date | string | number;

/**
 * Safely parse a date input to a Date object
 */
function toDate(input: DateInput): Date {
  if (input instanceof Date) return input;
  return new Date(input);
}

/**
 * Format date for display with date + time
 * Example: "Feb 10, 2024, 3:30 PM"
 */
export function formatDateTime(input: DateInput): string {
  const date = toDate(input);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format date for display (date only)
 * Example: "Feb 10, 2024"
 */
export function formatDate(input: DateInput): string {
  const date = toDate(input);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format date for short display (no year)
 * Example: "Feb 10"
 */
export function formatDateShort(input: DateInput): string {
  const date = toDate(input);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Format date with weekday (short, no year)
 * Example: "Mon, Feb 10"
 */
export function formatDateWithWeekday(input: DateInput): string {
  const date = toDate(input);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format date with full weekday for next send displays
 * Example: "Monday, Feb 10"
 */
export function formatNextSendDate(input: DateInput): string {
  const date = toDate(input);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format date range for display
 * Example: "Feb 10 — Feb 17, 2024"
 * If same year, only shows year at end
 */
export function formatDateRange(start: DateInput, end: DateInput): string {
  const startDate = toDate(start);
  const endDate = toDate(end);

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  if (startYear === endYear) {
    // Same year: "Feb 10 — Feb 17, 2024"
    const startStr = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const endStr = endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startStr} — ${endStr}`;
  } else {
    // Different years: "Dec 28, 2023 — Feb 10, 2024"
    const startStr = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const endStr = endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startStr} — ${endStr}`;
  }
}

/**
 * Format date for chart tooltip with full date
 * Example: "Feb 10, 2024"
 */
export function formatChartDate(input: DateInput): string {
  return formatDate(input);
}

/**
 * Format date for chart x-axis labels (compact)
 * Example: "Feb 10"
 */
export function formatChartLabel(input: DateInput): string {
  return formatDateShort(input);
}
